<template>
  <div
    :class="`sidebar-resume sidebar-resume--${
      contained ? 'contained' : 'alone'
    }`"
  >
    <!--<h3 class="h3">Riepilogo tariffa</h3>-->
    <div v-for="(flight_offer, index) in flight_offers" :key="'price_' + index">
      <div class="sidebar-resume__title">
        <h5 class="vb-heading">La tua scelta</h5>
        <checkout-timer
          v-if="has_checkout_timer"
          :start="checkout_timer_start"
          :amount="checkout_timer_amount"
          @expired="show_expired()"
        />
      </div>
      <div class="sidebar-resume__blocks">
        <!-- base -->
        <resume-block
          title="Tariffa:"
          :title_content="{
            icon: {
              name: 'flight_ticket',
              size: 16,
              color: sass('primary'),
            },
            text: branded_fare(flight_offer),
          }"
        >
          <template #default>
            <resume-row
              v-for="(group, age) in travelers_by_age(flight_offer)"
              :key="`group:${age}`"
              :label="`${group.list.length} ${transcode_traveler_type(
                { traveler_type: age },
                group.list.length,
              )}`"
              :content="group.total"
              :currency_symbol="flight_offer.price.currencySymbol"
              :currency="flight_offer.price.currency"
            />
            <resume-row
              label="Tasse e spese"
              :content="get_total_tax(flight_offer)"
              :currency_symbol="flight_offer.price.currencySymbol"
              :currency="flight_offer.price.currency"
              info="<b>Perché una commissione di servizio?</b> Questa cifra ci permette di offrire e mantenere un'integrazione diretta con il tuo Welfare Provider e un'assistenza da record in caso di necessità. È l'unico costo che applichiamo per garantirti un processo di prenotazione fluido e sicuro."
            />
          </template>
          <template #footer>
            <resume-row
              label="Totale"
              classes="semibold"
              :content="get_base_total(flight_offer)"
              :currency_symbol="flight_offer.price.currencySymbol"
              :currency="flight_offer.price.currency"
            />
          </template>
        </resume-block>

        <!-- included bags -->
        <resume-block
          title="Bagagli inclusi nella tariffa"
          info="I bagagli indicati si riferiscono al totale per tutti i passeggeri e tratte. Verifica sul sito della compagnia peso, misure e condizioni esatte."
        >
          <template #default>
            <resume-row
              label="Bagaglio personale"
              :content="get_all_hand_bags(flight_offer)"
              icon="backpack"
              info="È un piccolo bagaglio personale, come uno zaino o una borsa, da tenere sotto il sedile."
            />
            <resume-row
              v-if="shows_included_cabin_bags(flight_offer)"
              label="Bagaglio a mano"
              :content="get_all_cabin_bags(flight_offer)"
              icon="trolley"
            />
            <resume-row
              v-else-if="
                flight_offer_has_upsell && branded_fare(flight_offer).length
              "
              label="Bagaglio a mano"
              :content="
                flight_offer.travelerPricings.length *
                (flight_offer.source === 'GDS' ? 1 : 0)
              "
              icon="trolley"
            >
              <template #content>
                <vb-icon
                  name="info"
                  :size="16"
                  :color="'#AAA'"
                  v-b-popover.hover.top="
                    `Controlla nella descrizione della tariffa ${
                      branded_fare(flight_offer) || 'che hai selezionato'
                    } quanti bagagli a mano sono inclusi.`
                  "
                >
                </vb-icon>
              </template>
            </resume-row>
            <resume-row
              label="Bagaglio da stiva"
              :content="get_all_included_bags(flight_offer)"
              icon="luggage"
            />
          </template>
        </resume-block>
      </div>
      <div
        v-if="paid_additional_services_by_offer[flight_offer.id].length > 0"
        class="d-flex flex-column"
        style="gap: 30px; padding-top: 30px"
      >
        <div
          v-for="(
            additional_service, additional_service_index
          ) in paid_additional_services_by_offer[flight_offer.id]"
          :key="`additional_service:${additional_service.type}:${additional_service_index}`"
        >
          <resume-block
            :title="
              capitalize_string(
                transcode_additional_services(additional_service.type, 99),
              )
            "
            :info="
              capitalize_string(
                get_additional_service_tooltip(additional_service.type),
              )
            "
          >
            <template
              v-if="additional_service.type === 'CHECKED_BAGS'"
              #default
            >
              <resume-row
                v-for="[key, extra_bag] in Object.entries(
                  additional_service.info || flexible_bags,
                )"
                :key="key"
                :label="
                  extra_bag.description ||
                  `Bagagli${
                    extra_bag.quantity * extra_bag.travelers.length === 1
                      ? 'o'
                      : ''
                  } in stiva`
                "
                :content="
                  extra_bag.content != null
                    ? extra_bag.content
                    : extra_bag.quantity * extra_bag.travelers.length
                "
                :icon="extra_bag.icon || 'luggage'"
              />
            </template>
            <template
              v-else-if="additional_service.type === 'SPORTS_EQUIPMENT'"
              #default
            >
              <resume-row
                v-for="[key, extra_bag] in Object.entries(
                  additional_service.info || {},
                )"
                :key="key"
                :label="extra_bag.description"
                :content="extra_bag.content"
                :icon="extra_bag.icon || 'luggage'"
              />
            </template>
            <template
              v-else-if="additional_service.type === 'SERVICES'"
              #default
            >
              <resume-row
                v-for="[key, extra_bag] in Object.entries(
                  additional_service.info || {},
                )"
                :key="key"
                :label="extra_bag.description"
                :content="extra_bag.content"
                :icon="extra_bag.icon || 'check_circle'"
              />
            </template>
            <template v-else-if="additional_service.type === 'SEATS'" #default>
              <resume-row
                v-for="(seats, key) in additional_service.info ||
                seats_per_segment(flight_offer, traveler_details)"
                :key="`seats:${key}`"
                :label="formatSegmentLabel(segmentsMap[key]) || key"
                icon="seats_choice"
              >
                <template #content>
                  <span
                    v-for="seat in seats"
                    :key="`seat:${key}:${seat.seatNumber}`"
                    v-b-popover.hover.top="
                      seat.traveler_name || `P${seat.travelerId}`
                    "
                    class="text-primary ml-1"
                  >
                    {{ seat.seatNumber }}
                  </span>
                </template>
              </resume-row>
            </template>
            <template v-else-if="additional_service.info" #default>
              <resume-row
                v-for="[key, item] in Object.entries(additional_service.info)"
                :key="key"
                :label="item.description"
                :content="item.content"
                :icon="item.icon || 'check_circle'"
              />
            </template>
            <template
              v-if="Number((additional_service.price || {}).total || 0)"
              #footer
            >
              <resume-row
                label="Totale"
                classes="semibold"
                :content="
                  Number(
                    apply_markup_to_number(
                      flight_offer,
                      (additional_service.price || {}).total || '0',
                    ),
                  )
                "
                :currency_symbol="flight_offer.price.currencySymbol"
                :currency="flight_offer.price.currency"
              />
            </template>
          </resume-block>
        </div>
      </div>

      <template v-if="!contained">
        <div id="resume-total" class="mt-5 d-flex justify-content-between">
          <h3 class="h3 mb-0">Totale</h3>
          <h3 class="h3 mb-0">
            {{ final_price(flight_offer) }}
          </h3>
        </div>

        <div>
          <p class="mb-2">
            Tariffa, tasse e spese amministrative per tutti i partecipanti
          </p>
        </div>

        <div v-if="post_sell_notice.text">
          <p>
            {{ post_sell_notice.text }}
          </p>
        </div>
      </template>
    </div>

    <vb-modal
      v-if="has_checkout_timer"
      :vb_options="{
        id: expired_modal_id,
        title: 'Opss! La sessione è scaduta',
        body_class: 'sidebar-resume__modal',
        no_close_on_backdrop: true,
        no_close_on_esc: true,
        hide_header_close: true,
        hide_footer: true,
        centered: true,
        size: 'md',
      }"
    >
      <p>
        Questa proposta era disponibile per un tempo limitato. Per aggiornare
        disponibilità e prezzi serve una nuova ricerca.
      </p>
      <p>
        Chiudendo questa finestra verrai riportato alla pagina dei risultati.
      </p>
      <vb-button
        @click="handle_expired()"
        :vb_options="{
          text: 'Fai una nuova ricerca',
          variant: 'outline-info',
          block: true,
        }"
      />
    </vb-modal>
  </div>
</template>

<script>
import {
  get_markup,
  apply_markup_to_number,
} from "@liveflight/utilities/price";
import {
  transcode_additional_services,
  get_additional_service_tooltip,
  get_paid_additional_services,
} from "@liveflight/utilities/additional-services";
import { transcode_traveler_type } from "@liveflight/utilities/travelers";
import { taxes } from "@liveflight/resources/taxes";
import { get_bags, has_capability_for } from "@liveflight/providers";
import { make_price } from "@liveflight/providers/base/helpers";
import {
  getSegmentsFromFlightOffers,
  seats_per_segment,
} from "@liveflight/utilities/seat-map";
import {
  formatPrice,
  capitalize,
  formatSegmentLabel,
} from "@liveflight/utilities/mixins";
import { number, subtract, sum } from "mathjs";
import { mapState } from "vuex";
import SidebarResumeBlock from "@liveflight/components/blocks/resume/resume-block.vue";
import SidebarRow from "@liveflight/components/blocks/resume/resume-row.vue";
import CheckoutTimer from "@liveflight/components/blocks/resume/checkout-timer.vue";

export default {
  name: "resume",
  components: {
    "resume-block": SidebarResumeBlock,
    "resume-row": SidebarRow,
    "checkout-timer": CheckoutTimer,
  },
  methods: {
    transcode_additional_services,
    transcode_traveler_type,
    formatPrice,
    apply_markup_to_number,
    formatSegmentLabel,
    get_additional_service_tooltip,
    get_paid_additional_services,
    seats_per_segment,
    capitalize_string: capitalize,
    get_all_included_bags(flight_offer) {
      return get_bags(flight_offer, 0).included;
    },
    get_all_cabin_bags(flight_offer) {
      return get_bags(flight_offer, 0).cabin;
    },
    get_all_hand_bags(flight_offer) {
      return get_bags(flight_offer, 0).hand;
    },
    shows_included_cabin_bags(flight_offer) {
      return has_capability_for(flight_offer, "showIncludedCabinBags");
    },
    uncommitted_total(flight_offer) {
      const additions = Array.isArray(this.uncommitted_additions)
        ? this.uncommitted_additions
        : [];
      return additions.reduce((carry, entry) => {
        const { price = {}, amount = "0" } = entry || {};
        const value = price.total || price.grandTotal || amount;
        const marked_up = number(
          apply_markup_to_number(flight_offer, number(value)),
        );
        return sum(carry, marked_up);
      }, number(0));
    },
    final_price(flight_offer) {
      const base = Number(get_markup(flight_offer).mu_price) || 0;
      const uncommitted_total = sum(base, this.uncommitted_total(flight_offer));

      return `${flight_offer.price.currencySymbol} ${formatPrice(uncommitted_total)}`;
    },
    travelers_by_age(flight_offer) {
      return flight_offer.travelerPricings.reduce((groups, pricing) => {
        const previous = groups[pricing.travelerType] || { list: [], total: 0 };
        return {
          ...groups,
          [pricing.travelerType]: {
            list: [...previous.list, pricing],
            total: previous.total + number((pricing.price || {}).total || 0),
          },
        };
      }, {});
    },
    get_total_tax(flight_offer) {
      const tax = this.get_markup(flight_offer);
      return subtract(tax.mu_amount, tax.additional_services_mu_amount);
    },
    get_travelers_total(flight_offer) {
      return Object.values(this.travelers_by_age(flight_offer)).reduce(
        (total, elem) => total + elem.total,
        0,
      );
    },
    get_base_total(flight_offer) {
      return (
        this.get_travelers_total(flight_offer) +
        this.get_total_tax(flight_offer)
      );
    },
    branded_fare(flight_offer) {
      try {
        return this.capitalize_string(
          flight_offer.travelerPricings[0].fareDetailsBySegment[0].brandedFare,
        );
      } catch {
        return "";
      }
    },
    show_expired() {
      this.$bvModal.show(this.expired_modal_id);
    },
    handle_expired() {
      this.$bvModal.hide(this.expired_modal_id);
      this.$router.push({ name: "results" });
    },
  },
  data() {
    return {
      segmentsMap: {},
      expired_modal_id: `offer-expired:${this._uid}`,
    };
  },
  props: {
    flight_offers: {
      type: Array,
      default: () => {
        return [];
      },
    },
    post_sell_notice: {
      type: Object,
      default: () => {
        return {};
      },
    },
    contained: {
      type: Boolean,
      default: false,
    },
    uncommitted_additions: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState("liveflight", [
      "checkout_timer_start",
      "checkout_timer_amount",
    ]),
    flight_offer_has_upsell() {
      try {
        return this.$store.state.liveflight.flight_offer_has_upsell;
      } catch {
        return false;
      }
    },
    flexible_bags() {
      const flexible_bags = this.$store.state.liveflight.flexible_bags;

      const mapped = {};
      Object.entries(flexible_bags).forEach((entry) => {
        const traveler_index = entry[0];
        const itineraries = Object.entries(entry[1]);
        itineraries.forEach((itinerary) => {
          const itinerary_index = itinerary[0];
          const bags = Object.entries(itinerary[1]);
          bags.forEach((elem) => {
            const bag_info = elem[1];

            if (bag_info.added) {
              const bag = bag_info.bag;
              const prev = mapped[`${bag.name}:${bag.quantity}`] || {
                itineraries: [],
                travelers: [],
              };
              mapped[`${bag.name}:${bag.quantity}`] = {
                ...bag,
                itineraries: !prev.itineraries.includes(itinerary_index)
                  ? [...prev.itineraries, itinerary_index]
                  : prev.itineraries,
                travelers: !prev.travelers.includes(traveler_index)
                  ? [...prev.travelers, traveler_index]
                  : prev.travelers,
              };
            }
          });
        });
      });

      return mapped;
    },
    traveler_details() {
      return this.$store.state.liveflight.traveler_details;
    },
    has_checkout_timer() {
      return this.checkout_timer_start > 0;
    },
    paid_additional_services_by_offer() {
      const { currency = "" } = (this.flight_offers[0] || {}).price || {};
      const additions = Array.isArray(this.uncommitted_additions) ? this.uncommitted_additions : [];
      const normalized_additions = additions.map((entry) => {
        const { price, amount = "0" } = entry || {};
        if (price) return entry;
        return { ...entry, price: make_price(String(amount), currency) };
      });
      const by_offer = {};
      this.flight_offers.forEach((offer) => {
        const { id = "" } = offer || {};
        by_offer[id] = get_paid_additional_services(offer).concat(normalized_additions);
      });
      return by_offer;
    },
  },
  created() {
    this.get_markup = get_markup;
    this.taxes = taxes;
    this.segmentsMap = getSegmentsFromFlightOffers(this.flight_offers);
  },
};
</script>

<style lang="scss" scoped>
.sidebar-resume {
  padding: 2 * $grid-gutter-width/3 $grid-gutter-width/6 $grid-gutter-width/6
    $grid-gutter-width/6;

  &--contained {
    background: var(--white);
    padding: 2 * $grid-gutter-width/3;
    border-radius: $border-radius * 2;
    margin-bottom: 2 * $grid-gutter-width/3;
  }

  &__title {
    display: flex;
    flex-direction: column;
  }

  &__modal {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  p,
  .paragraph {
    font-size: 12px;
    margin-top: 10px;
    @include font-primary-regular;
  }

  &__blocks {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }
}
</style>
